<template>
	<div>
		<Loader showLottie v-if="!hasLoadedNodesData" class="flow-tree-loader" pointerEventsNone />
		<Col v-else-if="showGraphMigrationErrorMessage" class="tree-container flex-centerize">
			<h2 class="reg">{{ graphMigrationErrorMessage }}</h2>
			<AnemicButton @click="contactUsAboutFailedMigration" v-if="didGraphMigrationFail" tile class="px-3 mt-4 contact-us-button">
				Contact us
			</AnemicButton>
		</Col>
		<div v-else-if="workflowVersionNodes.length > 0" class="tree-container">
			<ReactFlowTree v-bind="flowTreeProps" />
		</div>
	</div>
</template>

<script>
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { applyReactInVue } from 'vuereact-combined';
import { mapGetters } from 'vuex';

import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

import FlowTreeMixin from './FlowTreeMixin';
import FlowTreeService from './flow-tree-service';
import { NodeTypes, EdgeTypes } from '@/components/FlowTree/flow-tree-service.const';
import ReactFlowTree, { ZOOM_DISABLE_ACTIVATION_KEY_CODE } from '@/react_app/components/ReactFlow/ReactFlowTree';
import { NODE_WIDTH, X_AXIS_SPACING } from '@/react_app/components/ReactFlow/hooks/react-flow-tree.const';
import { getAction } from '@/store/modules/actions/getters';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import { getContext } from '@/store/modules/contexts/getters';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { useNodeOutgoingEdges } from '@/store/modules/nodes/getters';
import { editNodeId } from '@/store/modules/selectedNode/getters';
import { isOfTypeFeature, isOfTypeTool, isNarrowBranchesLayout } from '@/store/modules/selectedWorkflow/getters';
import { doesSelectedWorkflowHasConfigurations } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { setEdgeToCreateNodeIn, addEndRunBlock } from '@/store/modules/treeBlocks/actions';
import {
	useIsNodeReachable,
	isMoveNodeActive,
	tempBlock,
	useWorkflowVersionGraph,
	useIsEdgeInBranch,
} from '@/store/modules/treeBlocks/getters';
import { rootNodeId as rootNodeIdGetter } from '@/store/modules/workflows/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import IntercomService from '@/utils/IntercomService';
import { arrayToObject } from '@/utils/helpers';
import * as WorkflowGraphService from '@/utils/workflow/workflow-graph-service';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { TEMP_NODE_ID_KEY_PREFIX } from '@/utils/workflow-node.const';

const _localCache = {};
const _clearLocalCache = () => {
	for (const key in _localCache) {
		if (_localCache.hasOwnProperty(key)) {
			delete _localCache[key];
		}
	}
};
const _getFromCache = (cacheProperty, key, valueFn) => (cacheProperty[key] ??= valueFn());

const FOCUS_ON_NODE_DELAY_MS = 30;

export default {
	name: 'FlowTree',
	components: { ReactFlowTree: applyReactInVue(ReactFlowTree) },
	props: {
		workflowVersionId: { type: Number },
		hasLoadedNodesData: { type: Boolean },
		zoomOnScroll: { type: Boolean, default: true },
		panOnScroll: { type: Boolean, default: false },
		panOnDrag: { type: Boolean, default: true },
		isInterfaceFocusedMode: { type: Boolean, default: false },
		isLeftSideDrawerOpen: { type: Boolean },
		isWideWidget: { type: Boolean },
		showMiniMap: { type: Boolean },
	},
	mixins: [FlowTreeMixin],
	computed: {
		...mapGetters([isConfigurationsOn.fullPath]),
		...mapGettersShorthand({
			getAction,
			tempBlock,
			editNodeId,
			getContext,
			isOfTypeTool,
			isOfTypeFeature,
			getServiceBySlug,
			isMoveNodeActive,
			rootNodeIdGetter,
			useIsNodeReachable,
			useNodeOutgoingEdges,
			isNarrowBranchesLayout,
			useWorkflowVersionGraph,
			doesSelectedWorkflowHasConfigurations,
			useIsEdgeInBranch,
		}),
		flowTreeProps() {
			const {
				edges,
				nodes,
				nodeToFocus,
				clusterSizeMap,
				isNarrowBranchesLayout,
				isInterfaceFocusedMode,
				panOnScroll,
				zoomOnScroll,
				panOnDrag,
				runFeedbackFlow,
				reCenterView: onRecenter,
				controlsPosition,
				showMiniMap,
				nonBranchesAndLoopsDag,
			} = this;
			const props = {
				edges,
				nodes,
				nodeToFocus,
				clusterSizeMap,
				isNarrowBranchesLayout,
				isInterfaceFocusedMode,
				zoomOnScroll,
				panOnScroll,
				panOnDrag,
				runFeedbackFlow,
				onRecenter,
				controlsPosition,
				showMiniMap,
			};
			if (!zoomOnScroll) {
				props.zoomActivationKeyCode = ZOOM_DISABLE_ACTIVATION_KEY_CODE;
			}
			if (this.isInterfaceFocusedMode) {
				props.nodes = nonBranchesAndLoopsDag.nodes;
				props.edges = nonBranchesAndLoopsDag.edges;
			}
			return props;
		},
		workflowVersion() {
			return this.workflow.versions.find((version) => version.id === this.workflowVersionId);
		},
		workflowVersionGraphMigrationStatus() {
			return this.workflowVersion?.graphed;
		},
		didGraphMigrationFail() {
			return this.workflowVersionGraphMigrationStatus === false;
		},
		showGraphMigrationErrorMessage() {
			return this.workflowVersion && !this.workflowVersionGraphMigrationStatus;
		},
		graphMigrationErrorMessage() {
			if (this.showGraphMigrationErrorMessage) {
				if (this.didGraphMigrationFail) {
					return 'Something went wrong with this feature version, and it is not editable anymore. sorry for the inconvenience.';
				} else {
					return 'This feature version is under maintenance, please return later.';
				}
			} else {
				return null;
			}
		},
		relevantClusterIds() {
			const everyLoopDirectChildren = this.allLoopStartNodes.map((loopStartNode) => this.getLoopStartOrderedChildrenIds(loopStartNode));
			const allBranchesDirectChildren = this.allBranchRootNodes
				.filter((node) => this.isNodeBranchRoot(node?.id))
				.map((branchRootNode) => this.getBranchOrderedChildren(branchRootNode));
			const allUniqueIdsToCalculateClusterWidth = [
				...new Set(
					[
						...everyLoopDirectChildren.flat(),
						...allBranchesDirectChildren.flat(),
						...this.allLoopStartNodes.map((node) => String(node?.id)),
						...this.allBranchRootNodes.map((node) => String(node?.id)),
					].filter((id) => id)
				),
			];
			return allUniqueIdsToCalculateClusterWidth;
		},
		clusterSizeMap() {
			_clearLocalCache();
			const allUniqueIdsToCalculateClusterWidth = this.relevantClusterIds;
			const res = {};
			if (isEmpty(this.workflowVersionNodesMap) || !this.dag) {
				return res;
			}
			allUniqueIdsToCalculateClusterWidth.forEach((id) => {
				const isTempBlock = WorkflowNodeService.isNodeTemp({ id });
				let node = isTempBlock ? this.tempBlock : this.workflowVersionNodesMap[id];
				if (isTempBlock) {
					const tempNodeEdge = this.edgesWithEndRunEdgeAndPreNodesRelatedEdges.find(({ id }) => id == node.edge);
					node = tempNodeEdge ? this.workflowVersionNodesMap[tempNodeEdge.target] : node;
				}
				const clusterEndNodeId = this.getClusterEndNodeId(node);
				const clusterEndNode = this.workflowVersionNodesMap[clusterEndNodeId];
				const fullClusterWidthNarrowMode = this.getFullClusterWidthNarrowMode(node, clusterEndNode);
				const sortedDirectChildren = this.getNodeOrderedChildrenIds(node);
				let partial;
				if (sortedDirectChildren.length < 2) {
					partial = fullClusterWidthNarrowMode;
				} else {
					const rightMostChildId = sortedDirectChildren.at(-1);
					const rightMostChild = rightMostChildId ? this.workflowVersionNodesMap[rightMostChildId] : null;
					const leftMostChildId = sortedDirectChildren[0];
					const leftMostChild = this.workflowVersionNodesMap[leftMostChildId];
					const leftOutbound = this.getDistanceBetweenNodeToLeftMostChild(leftMostChild, clusterEndNode);
					const rightOutbound = this.getDistanceBetweenNodeToRightMostChild(rightMostChild, clusterEndNode);
					partial = fullClusterWidthNarrowMode - (leftOutbound + rightOutbound + NODE_WIDTH);
				}

				res[id] = {
					partial,
					full: this.getFullClusterWidthWideMode(node, clusterEndNode),
					left: this.getDistanceBetweenNodeToLeftMostChild(node, clusterEndNode),
					right: this.getDistanceBetweenNodeToRightMostChild(node, clusterEndNode),
				};
			});

			return res;
		},
		canAddContextualActionNode() {
			// TODO-graph : check what happens when this is true, perhaps we should create a fake edge to connect it in the right place
			// only if workflow is add-on (button) and placement allows us to get contextual data and this node doesn't exists already or is orphan node
			return (
				this.hasElement &&
				this.isOfTypeFeature &&
				!this.workflowVersionNodes.some((node) => this.isContextualNode(node) && !WorkflowNodeService.isNodeOrphan(node))
			);
		},
		endRunBlock() {
			return this.lastNodeInMainPath?.service === this.endRunService?.id ? this.lastNodeInMainPath : undefined;
		},
		endRunService() {
			return this.getServiceBySlug(ServiceSlugs.EndRun);
		},
		isNoContextualNodesExists() {
			return this.workflowVersionNodes.filter((block) => !this.isContextualNode(block)).length < 2;
		},
		isConfigurationsOn() {
			const featureFlagStatus = this[isConfigurationsOn.fullPath];
			return featureFlagStatus && (this.workflow.setup_enabled || this.doesSelectedWorkflowHasConfigurations);
		},
		isEndRunReachable() {
			if (!this.endRunBlock?.id) {
				if (this.lastNodeInMainPath) {
					// fake end run node is reachable if the last node in main path is reachable
					return this.useIsNodeReachable({ node: this.lastNodeInMainPath });
				}
				return true;
			}
			return this.useIsNodeReachable({ node: this.endRunBlock });
		},
		workflowVersionGraph() {
			return this.useWorkflowVersionGraph(this.editWorkflow.id, this.workflowVersionId);
		},
		workflowVersionNodes() {
			return (this.workflowVersionGraph.nodes || []).filter(
				(node) => !node.is_notification_channel_node && !(node.is_orphan && this.isContextualNode(node))
			);
		},
		rootNodeId() {
			return this.rootNodeIdGetter(this.editWorkflow.id, this.workflowVersionId);
		},
		nodesWithTempBlock() {
			if (this.tempBlock) {
				return [...this.workflowVersionNodes, this.tempBlock];
			} else {
				return this.workflowVersionNodes;
			}
		},
		clientPreEdges() {
			const configurationsNextNodeId = this.isOfTypeFeature
				? NodeTypes.AddOnTrigger
				: this.isOfTypeTool
				? NodeTypes.ToolTrigger
				: this.rootNodeId;

			const res = [];
			if (this.isConfigurationsOn) {
				res.push({
					type: EdgeTypes.Invisible,
					source: NodeTypes.Setup,
					target: `${configurationsNextNodeId}`,
					id: `${NodeTypes.Setup}=>${configurationsNextNodeId}`,
				});
			}
			if (this.isOfTypeFeature) {
				res.push({
					type: EdgeTypes.Invisible,
					target: `${this.rootNodeId}`,
					source: NodeTypes.AddOnTrigger,
					id: `${NodeTypes.AddOnTrigger}=>${this.rootNodeId}`,
				});
			} else if (this.isOfTypeTool) {
				res.push({
					type: EdgeTypes.Invisible,
					target: `${this.rootNodeId}`,
					source: NodeTypes.ToolTrigger,
					id: `${NodeTypes.ToolTrigger}=>${this.rootNodeId}`,
				});
			}
			return res;
		},
		clientPreNodes() {
			const res = [];
			if (this.isConfigurationsOn) {
				res.push({
					id: NodeTypes.Setup,
					type: NodeTypes.Setup,
					data: { node: { name: 'Configurations', id: NodeTypes.Setup } },
				});
			}
			if (this.isOfTypeFeature) {
				res.push({
					id: NodeTypes.AddOnTrigger,
					type: NodeTypes.AddOnTrigger,
					data: { node: { name: 'Add-Ons', id: NodeTypes.AddOnTrigger } },
				});
			} else if (this.isOfTypeTool) {
				res.push({
					id: NodeTypes.ToolTrigger,
					type: NodeTypes.ToolTrigger,
					data: { workflow: this.editWorkflow, node: { name: 'Tool Trigger', id: NodeTypes.ToolTrigger } },
				});
			}
			return res;
		},
		showVirtualNodeForContextWithoutAction() {
			return this.canAddContextualActionNode && !this.getContext(this.element.context)?.action;
		},
		contextActionNode() {
			if (this.canAddContextualActionNode) {
				if (this.showVirtualNodeForContextWithoutAction) {
					return {
						id: NodeTypes.VirtualContextual,
						type: NodeTypes.VirtualContextual,
					};
				} else {
					return {
						id: NodeTypes.SkippedContextual,
						type: NodeTypes.SkippedContextual,
					};
				}
			}
			return null;
		},
		fakeEndRunNode() {
			return {
				id: NodeTypes.EndRun,
				type: NodeTypes.EndRun,
				data: { node: { id: NodeTypes.EndRun }, onEndRunClicked: this.endRunNodeClicked, isEndRunReachable: this.isEndRunReachable },
			};
		},
		loopFakePlaceholderNodes() {
			// loopConnectorPlaceholderInvisibleNodes - these are invisible placeholder on the FIXED AND NOT CHANGEABLE edge between loop start and end
			// fakeLoopStartToEndPlaceholderNodes - these are visible placeholder on the edge between loop start and end
			return [...this.fakeLoopStartToEndPlaceholderNodes, ...this.loopConnectorPlaceholderInvisibleNodes];
		},
		nodes() {
			const res = [
				...this.clientPreNodes,
				...this.fakeBranchesDivergedPlaceholderNodes,
				...this.loopFakePlaceholderNodes,
				this.contextActionNode,
			].filter((node) => !!node);
			this.nodesWithTempBlock.forEach((block) => {
				const outgoingHandles = this.getNodeOutgoingHandles(block);
				const clusterStartNodeId = this.getClusterStartNodeId(block);
				const orderedChildren = this.getNodeOrderedChildrenIds(block);
				res.push({
					id: `${block.id}`,
					type: this.getNodeType(block),
					style: { opacity: 0 },
					data: {
						node: block,
						outgoingHandles,
						orderedChildren,
						workflowVersionId: this.workflowVersionId,
						hasLoadedNodesData: this.hasLoadedNodesData,
						canAddContextualActionNode: this.canAddContextualActionNode,
						clusterStartNodeId: clusterStartNodeId ? `${clusterStartNodeId}` : null,
					},
				});
			});
			if (!this.endRunBlock) {
				res.push(this.fakeEndRunNode);
			}

			return res;
		},
		workflowVersionEdges() {
			return this.workflowVersionGraph.edges || [];
		},
		mainPathGraph() {
			const mainGraphPath = WorkflowGraphService.iterateFlat(this.dag, String(this.rootNodeId));
			const nodes = this.nodesWithTempBlock.filter((node) => mainGraphPath.includes(String(node.id)));
			const nodeIds = nodes.map((node) => node.id);
			const edges = this.workflowVersionEdges.filter((edge) => nodeIds.includes(edge.source) && nodeIds.includes(edge.target));
			return { edges, nodes };
		},
		endRunFakeEdge() {
			if (this.endRunBlock) {
				return null;
			} else {
				if (!this.lastNodeInMainPath) {
					return null;
				}
				return {
					target: NodeTypes.EndRun,
					onClick: this.onPlusClick,
					source: `${this.lastNodeInMainPath.id}`,
					id: `${this.lastNodeInMainPath.id}=>${NodeTypes.EndRun}`,
				};
			}
		},
		lastNodeInMainPath() {
			const res = WorkflowGraphService.getLastReachableNodeInTreeThatIsNotInBranch(this.mainPathGraph.nodes, this.mainPathGraph.edges);
			if (this.isContextElementNode(res)) {
				return this.contextActionNode || res;
			}
			return res;
		},
		edgesWithReplacedSpecialClusterEdgesPlaceholders() {
			const shouldReplaceEdgeWithPlaceholder = (edge) =>
				this.allDivergedEdgesBetweenBranchRootAndConsolidate.some((divergedEdge) => divergedEdge.id === edge.id) ||
				this.allRegularEdgesBetweenLoopStartAndEnd.some((loopEdge) => loopEdge.id == edge.id) ||
				edge.type === EdgeTypes.LoopConnector;
			const placeholderEdges = [];
			const relevantEdges = this.edgesWithEndRunEdgeAndPreNodesRelatedEdges.filter((edge) => {
				if (shouldReplaceEdgeWithPlaceholder(edge)) {
					const clusterRootId = edge.source;
					const clusterEndId = edge.target;
					const placeholderNodeId =
						edge.type === EdgeTypes.LoopConnector
							? `${EdgeTypes.LoopConnector}=>${edge.id}`
							: `${edge.id}=>${clusterRootId}=>${clusterEndId}`;
					const edgeType = edge.type === EdgeTypes.LoopConnector ? EdgeTypes.LoopConnector : EdgeTypes.Diverge;
					const incomingBranchPlaceholderEdge = {
						...edge,
						type: edgeType,
						source: `${clusterRootId}`,
						target: `${placeholderNodeId}`,
						id: `${clusterRootId}${TEMP_NODE_ID_KEY_PREFIX}${placeholderNodeId}`,
					};
					const outgoingBranchPlaceholderEdge = {
						type: EdgeTypes.Next,
						source: `${placeholderNodeId}`,
						target: `${clusterEndId}`,
						id: `${placeholderNodeId}${TEMP_NODE_ID_KEY_PREFIX}${clusterEndId}`,
					};

					placeholderEdges.push(incomingBranchPlaceholderEdge, outgoingBranchPlaceholderEdge);
					return false;
				} else {
					return true;
				}
			});
			const res = [...relevantEdges, ...placeholderEdges].filter((edge) => edge);
			return res;
		},
		workflowVersionEdgesWithContextualEdges() {
			const edgesWithClustersPatch = this.workflowVersionEdges;
			if (this.contextActionNode) {
				let contextNodeOutgoingEdge = null;
				let actionContextOutgoingEdge = null;
				const edgesWithoutElementOutgoingEdge = edgesWithClustersPatch.filter(({ source }) => source !== this.contextElementNode?.id);
				const contextActionNodeId = this.contextActionNode.id;
				const contextElementNodeId = this.contextElementNode.id;
				const originalOutgoingElementEdge = edgesWithClustersPatch.find(({ source }) => source == this.contextElementNode.id);
				contextNodeOutgoingEdge = {
					type: EdgeTypes.Default,
					source: contextElementNodeId,
					target: `${contextActionNodeId}`,
					id: `${contextElementNodeId}=>${contextActionNodeId}`,
				};
				if (originalOutgoingElementEdge) {
					actionContextOutgoingEdge = {
						type: EdgeTypes.Next,
						source: contextActionNodeId,
						id: `${originalOutgoingElementEdge.id}`,
						target: `${originalOutgoingElementEdge.target}`,
					};
				}

				return contextNodeOutgoingEdge
					? [...edgesWithoutElementOutgoingEdge, contextNodeOutgoingEdge, actionContextOutgoingEdge]
					: edgesWithClustersPatch;
			}
			return edgesWithClustersPatch;
		},
		loopConnectorPlaceholderInvisibleNodes() {
			return this.loopClusters.map(({ loopStart, loopEnd }) => {
				const nodeId = `${EdgeTypes.LoopConnector}=>${loopStart.id}=>${loopEnd.id}`;
				return { id: nodeId, /*position: { x: 0, y: 0 },*/ type: NodeTypes.LoopConnector, data: { node: { id: nodeId } } };
			});
		},
		edgesWithEndRunEdgeAndPreNodesRelatedEdges() {
			return [...this.clientPreEdges, ...this.workflowVersionEdgesWithContextualEdges, this.endRunFakeEdge].filter((edge) => edge);
		},
		contextElementNode() {
			if (this.hasElement) {
				return this.workflowVersionNodesMap[this.rootNodeId];
			}
			return null;
		},
		edges() {
			const edgesWithoutTempNodeEdges = this.edgesWithReplacedSpecialClusterEdgesPlaceholders.filter(
				({ id }) => String(id) !== String(this.tempBlock?.edge)
			);
			let tempNodeIncomingEdge = null;
			let tempNodeOutgoingEdge = null;
			if (this.tempBlock) {
				const tempNodeEdge = this.edgesWithEndRunEdgeAndPreNodesRelatedEdges.find(({ id }) => id == this.tempBlock?.edge);
				tempNodeIncomingEdge = {
					...tempNodeEdge,
					type: tempNodeEdge.type,
					target: this.tempBlock.id,
					source: `${tempNodeEdge.source}`,
					id: `${tempNodeEdge.source}=>${this.tempBlock.id}`,
				};
				tempNodeOutgoingEdge = {
					type: EdgeTypes.Next,
					source: this.tempBlock.id,
					target: `${tempNodeEdge.target}`,
					id: `${this.tempBlock.id}=>${tempNodeEdge.target}`,
				};
			}
			const edgesWithTempNodeEdges = this.tempBlock
				? [...edgesWithoutTempNodeEdges, tempNodeIncomingEdge, tempNodeOutgoingEdge].filter((edge) => edge)
				: this.edgesWithReplacedSpecialClusterEdgesPlaceholders;

			const allGraphEdges = edgesWithTempNodeEdges.map((edge) => {
				const edgeType = this.getEdgeType(edge);
				const isEdgeOfContextualNode = this.element?.action_context_node === edge.target;
				const isEdgeAllowedToEdit = this.isAllowedToEdit && !isEdgeOfContextualNode;
				/**
				 * TODO :: doesn't work, if branch of edge is empty, it should be possible to end run from it
				 */
				const isEndRunNodePossible = this.useIsEdgeInBranch(edge.id);
				return FlowTreeService.buildReactFlowEdge(
					edge,
					edgeType,
					isEdgeAllowedToEdit,
					this.onPlusClick,
					this.isMoveNodeActive,
					isEndRunNodePossible
				);
			});
			const allEdgesOnCanvas = allGraphEdges.filter((edge) => edge && edge.type !== EdgeTypes.ConsolidatedBy);
			return allEdgesOnCanvas;
		},
		consolidatedByEdges() {
			return this.workflowVersionEdges.filter((edge) => edge.type === EdgeTypes.ConsolidatedBy);
		},
		workflowVersionNodesMap() {
			return arrayToObject(this.workflowVersionNodes);
		},
		workflowVersionEdgesMap() {
			return arrayToObject(this.workflowVersionEdges);
		},
		dag() {
			return WorkflowGraphService.createWorkflowGraph(this.workflowVersionNodes, this.workflowVersionEdges, this.getAction);
		},
		nonBranchesAndLoopsDag() {
			const dag = WorkflowGraphService.createWorkflowGraph(this.nodes, this.edges, this.getAction);
			this.filterMainGraphEntries(
				this.branchesClusters.map(({ branchRoot, branchConsolidate }) => ({ startNode: branchRoot, endNode: branchConsolidate }))
			).forEach(({ startNode, endNode }) => this.replacePathWithNode(dag, startNode, endNode, 'branch'));
			this.filterMainGraphEntries(this.loopClusters.map(({ loopStart, loopEnd }) => ({ startNode: loopStart, endNode: loopEnd }))).forEach(
				({ startNode, endNode }) => this.replacePathWithNode(dag, startNode, endNode, 'loop')
			);
			return {
				nodes: Array.from(dag.nodeEntries()).map((n) => n.attributes.nodeObject),
				edges: Array.from(dag.edgeEntries()).map((e) => e.attributes.edgeObject),
			};
		},
		loopClusters() {
			const res = [];
			const allLoopConnectorEdges = this.workflowVersionEdges.filter(({ type }) => type === EdgeTypes.LoopConnector);
			allLoopConnectorEdges.forEach((edge) => {
				const loopStart = this.workflowVersionNodesMap[edge.source];
				const loopEnd = this.workflowVersionNodesMap[edge.target];
				if (loopStart && loopEnd) {
					res.push({ loopStart, loopEnd });
				}
			});

			return res;
		},
		allRegularEdgesBetweenLoopStartAndEnd() {
			const res = [];
			this.loopClusters.map(({ loopStart, loopEnd }) => {
				const regularEdgeBetweenLoopStartAndEnd = this.workflowVersionEdges.find(
					(edge) =>
						edge.id !== this.tempBlock?.edge &&
						edge.type !== EdgeTypes.LoopConnector &&
						edge.source == loopStart.id &&
						edge.target == loopEnd.id
				);
				if (regularEdgeBetweenLoopStartAndEnd) {
					res.push(regularEdgeBetweenLoopStartAndEnd);
				}
			});
			return res;
		},
		fakeLoopStartToEndPlaceholderNodes() {
			// these are visible placeholder on the edge between loop start and end
			return this.allRegularEdgesBetweenLoopStartAndEnd.map((edge) => {
				const nodeId = `${edge.id}=>${edge.source}=>${edge.target}`;
				return {
					id: nodeId,
					type: NodeTypes.EmptyNodePlusPlaceholder,
					data: {
						node: { id: nodeId },
						edge,
						onClick: (shortcut) => {
							this.onPlusClick(edge, shortcut);
						},
						isMoveNodeActive: this.isMoveNodeActive,
					},
				};
			});
		},
		branchesClusters() {
			const res = [];
			this.consolidatedByEdges.map((edge) => {
				const branchRoot = this.workflowVersionNodesMap[edge.source];
				const branchConsolidate = this.workflowVersionNodesMap[edge.target];
				res.push({ branchRoot, branchConsolidate });
			});
			return res;
		},
		allBranchRootNodes() {
			return this.branchesClusters.map(({ branchRoot }) => branchRoot);
		},
		allDivergedEdgesBetweenBranchRootAndConsolidate() {
			let res = [];
			const divergedEdges = this.workflowVersionEdges.filter(
				(edge) => edge.type === EdgeTypes.Diverge && String(edge.id) !== String(this.tempBlock?.edge)
			);
			this.branchesClusters.map(({ branchRoot, branchConsolidate }) => {
				const clusterDivergedEdges = divergedEdges.filter(
					(edge) => edge.source === branchRoot?.id && edge.target === branchConsolidate?.id
				);
				res = [...res, ...clusterDivergedEdges];
			});
			return res;
		},
		fakeBranchesDivergedPlaceholderNodes() {
			return this.allDivergedEdgesBetweenBranchRootAndConsolidate
				.filter((edge) => this.tempBlock?.edge != edge.id)
				.map((edge) => {
					const nodeId = `${edge.id}=>${edge.source}=>${edge.target}`;
					return {
						id: nodeId,
						type: NodeTypes.EmptyNodePlusPlaceholder,
						data: {
							node: { id: nodeId },
							edge,
							onClick: (shortcut) => {
								this.onPlusClick(edge, shortcut);
							},
							isMoveNodeActive: this.isMoveNodeActive,
						},
					};
				});
		},
		allLoopNodes() {
			return this.workflowVersionNodes.filter((node) => WorkflowNodeService.isLoop(node));
		},
		allLoopStartNodes() {
			return this.allLoopNodes.filter(this.isLoopStart);
		},
		allEndLoopNodes() {
			return this.allLoopNodes.filter(this.isLoopEnd);
		},
		endRunNodeIds() {
			return this.workflowVersionNodes.filter((node) => WorkflowNodeService.isEndRun(node)).map(({ id }) => id);
		},
		controlsPosition() {
			if (this.isWideWidget || this.isInterfaceFocusedMode) {
				return 'none';
			}
			return this.isLeftSideDrawerOpen ? 'top-right' : 'top-left';
		},
	},
	methods: {
		...mapActionsShorthand({ setEdgeToCreateNodeIn, addEndRunBlock }),
		contactUsAboutFailedMigration() {
			IntercomService.showNewMessage(
				`I need help with my feature version, feature ID - ${this.workflow.id}, version ID - ${this.workflowVersionId}`
			);
		},
		isLoopEnd(node) {
			const actionData = this.getAction(node.action);
			return WorkflowNodeService.isLoopEnd(node, actionData);
		},
		isNodeClusterStart(node) {
			return this.isNodeBranchRoot(node.id) || this.isLoopStart(node);
		},
		getDistanceBetweenNodeToRightMostChild(node, clusterEndNode) {
			const cache = _getFromCache(_localCache, 'mostright', () => ({}));
			const key = `${node?.id}-${clusterEndNode?.id}`;
			return _getFromCache(cache, key, () => this._getDistanceBetweenNodeToRightMostChild(node, clusterEndNode));
		},
		_getDistanceBetweenNodeToRightMostChild(node, clusterEndNode) {
			if (!node || !clusterEndNode) {
				return 0;
			}
			const pathsFromNodeToClusterEnd = this.getAllSimplePathsBetweenNodeAndClusterEnd(node, clusterEndNode.id);
			const uniqueNodesInPath = [...new Set(pathsFromNodeToClusterEnd.flat().map((node) => node.id))];
			const allClusterStartNodes = uniqueNodesInPath.filter(
				(id) => id !== node.id && this.isNodeClusterStart(this.workflowVersionNodesMap[id])
			);
			if (!this.isNodeClusterStart(node)) {
				const maxRightFromAnyClusterStart = allClusterStartNodes.reduce((acc, id) => {
					const maxRight = this.getDistanceBetweenNodeToRightMostChild(this.workflowVersionNodesMap[id], clusterEndNode);
					return Math.max(acc, maxRight);
				}, 0);
				return maxRightFromAnyClusterStart;
			} else {
				const sortedDirectChildren = this.getNodeOrderedChildrenIds(node);
				const amountOfChildren = sortedDirectChildren.length;
				if (allClusterStartNodes.length === 0) {
					// a branch root, without inner clusters
					if (amountOfChildren === 0) {
						return 0;
					}
					const spacesBetweenNodes = amountOfChildren - 1;
					const fullSize = amountOfChildren * NODE_WIDTH + spacesBetweenNodes * X_AXIS_SPACING - NODE_WIDTH;
					return fullSize / 2;
				} else {
					const allMiddleClustersFullLengths = allClusterStartNodes.reduce((acc, id, index) => {
						if (index === 0 || index === allClusterStartNodes.length - 1) {
							return acc;
						}
						const clusterStartNode = this.workflowVersionNodesMap[id];
						const clusterWidth = this.getFullClusterWidthNarrowMode(clusterStartNode, clusterEndNode);
						return acc + clusterWidth + X_AXIS_SPACING;
					}, 0);
					const leftMostChildDistanceToRight = this.getDistanceBetweenNodeToRightMostChild(
						this.workflowVersionNodesMap[sortedDirectChildren[0]],
						clusterEndNode
					);
					const rightMostChildId = sortedDirectChildren[sortedDirectChildren.length - 1];
					const rightMostChild = this.workflowVersionNodesMap[rightMostChildId];
					const rightMostChildDistanceToRight = this.getDistanceBetweenNodeToRightMostChild(rightMostChild, clusterEndNode);
					const rightMostChildDistanceToLeft = this.getDistanceBetweenNodeToLeftMostChild(rightMostChild, clusterEndNode);
					const centerOfContent =
						(leftMostChildDistanceToRight + X_AXIS_SPACING + NODE_WIDTH + allMiddleClustersFullLengths + rightMostChildDistanceToLeft) / 2;
					return centerOfContent + rightMostChildDistanceToRight;
				}
			}
		},
		getDistanceBetweenNodeToLeftMostChild(node, clusterEndNode) {
			const cache = _getFromCache(_localCache, 'mostleft', () => ({}));
			const key = `${node?.id}-${clusterEndNode?.id}`;
			return _getFromCache(cache, key, () => this._getDistanceBetweenNodeToLeftMostChild(node, clusterEndNode));
		},
		_getDistanceBetweenNodeToLeftMostChild(node, clusterEndNode) {
			if (!node || !clusterEndNode) {
				return 0;
			}
			const pathsFromNodeToClusterEnd = this.getAllSimplePathsBetweenNodeAndClusterEnd(node, clusterEndNode.id);
			const uniqueNodesInPath = [...new Set(pathsFromNodeToClusterEnd.flat().map((node) => node.id))];
			const allClusterStartNodes = uniqueNodesInPath.filter(
				(id) => id !== node.id && this.isNodeClusterStart(this.workflowVersionNodesMap[id])
			);
			if (!this.isNodeClusterStart(node)) {
				const maxLeftFromAnyClusterStart = allClusterStartNodes.reduce((acc, id) => {
					const maxLeft = this.getDistanceBetweenNodeToLeftMostChild(this.workflowVersionNodesMap[id], clusterEndNode);
					return Math.max(acc, maxLeft);
				}, 0);
				return maxLeftFromAnyClusterStart;
			} else {
				const sortedDirectChildren = this.getNodeOrderedChildrenIds(node);
				const amountOfChildren = sortedDirectChildren.length;
				if (allClusterStartNodes.length === 0) {
					// a branch root, without inner clusters
					if (amountOfChildren === 0) {
						return 0;
					}
					const spacesBetweenNodes = amountOfChildren - 1;
					const fullSize = amountOfChildren * NODE_WIDTH + spacesBetweenNodes * X_AXIS_SPACING - NODE_WIDTH;
					return fullSize / 2;
				} else {
					const allMiddleClustersFullLengths = sortedDirectChildren.reduce((acc, id, index) => {
						if (index === 0 || index === sortedDirectChildren.length - 1) {
							return acc;
						}
						const clusterStartNode = this.workflowVersionNodesMap[id];
						const clusterWidth = this.getFullClusterWidthNarrowMode(clusterStartNode, clusterEndNode);
						return acc + clusterWidth + X_AXIS_SPACING;
					}, 0);
					const rightMostChildDistanceToLeft = this.getDistanceBetweenNodeToLeftMostChild(
						this.workflowVersionNodesMap[sortedDirectChildren[sortedDirectChildren.length - 1]],
						clusterEndNode
					);
					const leftMostChildId = sortedDirectChildren[0];
					const leftMostChild = this.workflowVersionNodesMap[leftMostChildId];
					const leftMostChildDistanceToLeft = this.getDistanceBetweenNodeToLeftMostChild(leftMostChild, clusterEndNode);
					const leftMostChildDistanceToRight = this.getDistanceBetweenNodeToRightMostChild(leftMostChild, clusterEndNode);
					const centerOfContent =
						(leftMostChildDistanceToRight + X_AXIS_SPACING + NODE_WIDTH + allMiddleClustersFullLengths + rightMostChildDistanceToLeft) / 2;
					return centerOfContent + leftMostChildDistanceToLeft;
				}
			}
		},
		getFullClusterWidthWideMode(node, clusterEndNode) {
			const cache = _getFromCache(_localCache, 'clusterfullw', () => ({}));
			const key = `${node?.id}-${clusterEndNode?.id}`;
			return _getFromCache(cache, key, () => this._getFullClusterWidthWideMode(node, clusterEndNode));
		},
		_getFullClusterWidthWideMode(node, clusterEndNode) {
			if (!node || !clusterEndNode) {
				return NODE_WIDTH;
			}
			const allPathsBetweenNodeToClusterEnd = this.getAllSimplePathsBetweenNodeAndClusterEnd(node, clusterEndNode.id);
			if (this.isNodeClusterStart(node)) {
				const sortedDirectChildren = this.getNodeOrderedChildrenIds(node);
				const widestInnerPathCluster = sortedDirectChildren.reduce((acc, id) => {
					const isNodeTemp = WorkflowNodeService.isNodeTemp({ id });
					let internalNode = isNodeTemp ? this.tempBlock : this.workflowVersionNodesMap[id];
					if (isNodeTemp) {
						const tempBlockEdge = this.edgesWithEndRunEdgeAndPreNodesRelatedEdges.find(({ id }) => id == internalNode?.edge);
						internalNode = tempBlockEdge ? this.workflowVersionNodesMap[tempBlockEdge.target] : internalNode;
					}
					const internalNodeFullClusterWidth = this.getFullClusterWidthWideMode(internalNode, clusterEndNode);
					return Math.max(acc, internalNodeFullClusterWidth);
				}, 0);
				const spacesBetweenNodes = sortedDirectChildren.length - 1;
				return widestInnerPathCluster * sortedDirectChildren.length + spacesBetweenNodes * X_AXIS_SPACING;
			}
			const uniqueNodesInPath = [...new Set(allPathsBetweenNodeToClusterEnd.flat().map((node) => node.id))];
			const allClusterStartNodes = uniqueNodesInPath.filter((id) => this.isNodeClusterStart(this.workflowVersionNodesMap[id]));
			const maxClusterWidthInPathToConsolidate = allClusterStartNodes.reduce((acc, id) => {
				const size = this.getFullClusterWidthWideMode(this.workflowVersionNodesMap[id], clusterEndNode);
				return Math.max(acc, size);
			}, 0);
			return maxClusterWidthInPathToConsolidate || NODE_WIDTH;
		},
		getFullClusterWidthNarrowMode(node, clusterEndNode) {
			const cache = _getFromCache(_localCache, 'clusterfulln', () => ({}));
			const key = `${node?.id}-${clusterEndNode?.id}`;
			return _getFromCache(cache, key, () => this._getClusterWidth(node, clusterEndNode));
		},
		_getClusterWidth(node, clusterEndNode) {
			if (!node || !clusterEndNode) {
				return NODE_WIDTH;
			}
			const allPathsBetweenNodeToClusterEnd = this.getAllSimplePathsBetweenNodeAndClusterEnd(node, clusterEndNode.id);
			if (this.isNodeClusterStart(node)) {
				const sortedDirectChildren = this.getNodeOrderedChildrenIds(node);
				const res = sortedDirectChildren.reduce((acc, id) => {
					const isNodeTemp = WorkflowNodeService.isNodeTemp({ id });
					let internalNode = isNodeTemp ? this.tempBlock : this.workflowVersionNodesMap[id];
					if (isNodeTemp) {
						const tempBlockEdge = this.edgesWithEndRunEdgeAndPreNodesRelatedEdges.find(({ id }) => id == internalNode?.edge);
						internalNode = tempBlockEdge ? this.workflowVersionNodesMap[tempBlockEdge.target] : internalNode;
					}
					const internalNodeFullClusterWidth = this.getFullClusterWidthNarrowMode(internalNode, clusterEndNode);
					return acc + internalNodeFullClusterWidth;
				}, 0);
				const spacesBetweenNodes = sortedDirectChildren.length - 1;
				return res + spacesBetweenNodes * X_AXIS_SPACING;
			}
			const uniqueNodesInPath = [...new Set(allPathsBetweenNodeToClusterEnd.flat().map((node) => node.id))];
			const allClusterStartNodes = uniqueNodesInPath.filter((id) => this.isNodeClusterStart(this.workflowVersionNodesMap[id]));
			const maxClusterWidthInPathToConsolidate = allClusterStartNodes.reduce((acc, id) => {
				const size = this.getFullClusterWidthNarrowMode(this.workflowVersionNodesMap[id], clusterEndNode);
				return Math.max(acc, size);
			}, 0);
			return maxClusterWidthInPathToConsolidate || NODE_WIDTH;
		},
		getAllSimplePathsBetweenNodeAndClusterEnd(node, clusterEndNodeId) {
			return WorkflowGraphService.getAllSimplePaths(this.dag, node.id, clusterEndNodeId);
		},
		getNodeClusterStartNode(node) {
			const isNodeBranchConsolidate = this.isNodeBranchConsolidate(node.id);
			const isNodeLoopEnd = this.allEndLoopNodes.some((loopEndNode) => loopEndNode.id == node.id);
			const isRootNode = node.id === this.rootNodeId;
			if (isRootNode || isNodeBranchConsolidate || isNodeLoopEnd) {
				return null;
			}
			const isBranchRoot = this.isNodeBranchRoot(node.id);
			const isLoopRoot = this.isNodeLoopStart(node.id);
			if (isBranchRoot || isLoopRoot) {
				return node;
			}
			const nodeIncomingEdge = this.workflowVersionEdges.find((edge) => edge.target === node.id);
			if (nodeIncomingEdge) {
				return this.getNodeClusterStartNode(this.workflowVersionNodesMap[nodeIncomingEdge.source]);
			}
			return null;
		},
		getClusterEndNodeId(clusterRootNode) {
			if (!clusterRootNode) {
				return null;
			}
			const isBranchRoot = this.isNodeBranchRoot(clusterRootNode.id);
			const isLoopRoot = this.isNodeLoopStart(clusterRootNode.id);
			if (!isBranchRoot && !isLoopRoot) {
				return this.getClusterEndNodeId(this.getNodeClusterStartNode(clusterRootNode));
			}
			if (isBranchRoot) {
				const edge = this.consolidatedByEdges.find((edge) => edge.source === clusterRootNode.id);
				return edge?.target;
			}
			if (isLoopRoot) {
				const loopEndNode = this.getLoopStartEndBlock(clusterRootNode);
				return loopEndNode?.id;
			}
			return null;
		},
		getClusterStartNodeId(clusterEndNode) {
			const isBranchConsolidate = this.isNodeBranchConsolidate(clusterEndNode.id);
			const isLoopEnd = this.allEndLoopNodes.some((node) => node.id == clusterEndNode.id);
			if (!isBranchConsolidate && !isLoopEnd) {
				return null;
			}
			if (isBranchConsolidate) {
				const edge = this.consolidatedByEdges.find((edge) => edge.target == clusterEndNode.id);
				return edge?.source;
			}
			if (isLoopEnd) {
				return this.getLoopEndStartBlock(clusterEndNode)?.id;
			}
			return null;
		},
		isNodeLoopStart(nodeId) {
			return this.allLoopStartNodes.some((loopStartNode) => loopStartNode.id == nodeId);
		},
		getLoopStartEndBlock(loopStartNode) {
			return this.loopClusters.find(({ loopStart }) => loopStart.id == loopStartNode.id)?.loopEnd;
		},
		getLoopEndStartBlock(loopEndNode) {
			return this.loopClusters.find(({ loopEnd }) => loopEnd.id == loopEndNode.id)?.loopStart;
		},
		isLoopStart(node) {
			if (WorkflowNodeService.isLoop(node)) {
				const actionData = this.getAction(node.action);
				return !WorkflowNodeService.isLoopEnd(node, actionData);
			}
			return false;
		},
		getNodeType(block) {
			if (this.isNodeBranchRoot(block.id)) {
				return NodeTypes.BranchRoot;
			}
			if (this.isNodeBranchConsolidate(block.id)) {
				return NodeTypes.BranchConsolidate;
			}
			if (WorkflowNodeService.isLoop(block)) {
				if (this.isLoopStart(block)) {
					return NodeTypes.LoopStart;
				} else {
					return NodeTypes.LoopEnd;
				}
			}
			if (this.isContextElementNode(block)) {
				return NodeTypes.Element;
			}
			return NodeTypes.Custom;
		},
		isContextElementNode(node) {
			return node?.id == this.rootNodeId && this.hasElement;
		},
		getNodeOutgoingHandles(block) {
			if (this.isNodeBranchRoot(block.id)) {
				return this.edges
					.filter((edge) => edge.source == block.id && edge.type === EdgeTypes.Diverge)
					.toSorted((a, b) => a.branch_path.order - b.branch_path.order)
					.map(({ id }) => id);
			} else if (WorkflowNodeService.isLoop(block)) {
				return [...this.edges.filter((edge) => edge.source == block.id).map(({ id }) => id)];
			}
			return [];
		},
		getBranchOrderedChildren(block) {
			return this.edges
				.filter((edge) => edge.source == block.id && edge.type === EdgeTypes.Diverge)
				.toSorted((a, b) => a.branch_path.order - b.branch_path.order)
				.map(({ target }) => target);
		},
		getLoopStartOrderedChildrenIds(loopStartNode) {
			const loopStartOutgoingEdges = this.edges.filter((edge) => edge.source == loopStartNode.id);
			return loopStartOutgoingEdges.toSorted((a) => (a.type === EdgeTypes.LoopConnector ? -1 : 1)).map(({ target }) => target);
		},
		getNodeOrderedChildrenIds(node) {
			if (this.isNodeBranchRoot(node?.id)) {
				return this.getBranchOrderedChildren(node);
			} else if (this.isNodeLoopStart(node?.id)) {
				return this.getLoopStartOrderedChildrenIds(node);
			} else {
				return this.edges.filter((edge) => edge.source == node?.id);
			}
		},
		isNodeBranchRoot(nodeId) {
			return this.consolidatedByEdges.some((edge) => edge.source === nodeId);
		},
		isNodeBranchConsolidate(nodeId) {
			return this.consolidatedByEdges.some((edge) => edge.target === nodeId);
		},
		getEdgeType(edge) {
			const sourceId = edge.source;
			const targetId = edge.target;
			const isTempNodeInvolved = this.tempBlock && (this.tempBlock.id === sourceId || this.tempBlock.id === targetId);
			const isOutGoingFromLoopStart = this.allLoopStartNodes.some((node) => node.id == sourceId);
			if (isOutGoingFromLoopStart && edge.type !== EdgeTypes.LoopConnector) {
				return EdgeTypes.Diverge;
			}
			const isTargetFakeEndNode = targetId === NodeTypes.EndRun;
			const shouldShowAnimatedEdge = isTargetFakeEndNode && !isTempNodeInvolved && this.isNoContextualNodesExists && this.isAllowedToEdit;
			const isFakeClientNodeInvolved = this.clientPreNodes.some((node) => node.id === sourceId || node.id === targetId);
			const isSourceNodeEndRun = this.endRunNodeIds.includes(sourceId);
			if (isFakeClientNodeInvolved || isSourceNodeEndRun) {
				return EdgeTypes.Invisible;
			} else if (shouldShowAnimatedEdge) {
				return EdgeTypes.AnimatedHelpEdge;
			} else {
				return edge.type || EdgeTypes.Next;
			}
		},
		async onPlusClick(edge, shortcut) {
			if (this.isMoveNodeActive) {
				try {
					const isServerEdge = FlowTreeService.isServerEdge(edge?.type);
					const edgeId = isServerEdge ? edge?.id : null;
					await this.moveStepLocationInTree({ edgeId });
				} catch (e) {
					this.$toast.error('Error occurred while moving step.');
					throw e;
				}
			} else {
				this.setEdgeToCreateNodeIn({ edgeId: edge.id, shortcutCommand: shortcut });
			}
		},
		async endRunNodeClicked() {
			if (this.isEndRunReachable) {
				if (this.endRunBlock) {
					this.onClickBlock(this.endRunBlock);
				} else {
					try {
						await this.addEndRunBlock();
					} catch {
						this.$toast.error('Error occurred while adding end run step, please try again later.');
					}
				}
			}
		},
		focusOnNode: debounce(function (nodeId) {
			this.nodeToFocus = nodeId ? String(nodeId) : null;
		}, FOCUS_ON_NODE_DELAY_MS),
		reCenterView() {
			if (!this.nodeToFocus) {
				const nodeIdToFocus = this.editNodeId || this.rootNodeId || this.workflowVersionNodes[0]?.id;
				if (nodeIdToFocus) {
					return this.focusOnNode(nodeIdToFocus);
				}
			}
			const nodeToRecenter = this.nodeToFocus;
			this.focusOnNode(null);
			if (nodeToRecenter) {
				setTimeout(() => {
					this.$nextTick(() => {
						this.focusOnNode(nodeToRecenter);
					});
				}, FOCUS_ON_NODE_DELAY_MS);
			}
		},
		isChildOfBranchRoot(node) {
			if (!node) {
				return false;
			}
			const edge = this.workflowVersionEdges.find(({ id }) => id == node.edge);
			const parentId = edge?.source;
			if (!parentId) {
				return false;
			}
			const parentNode = this.workflowVersionNodes.find(({ id }) => id == parentId);
			return this.isNodeBranchRoot(parentNode.id);
		},
		isChildOfLoopStart(node) {
			if (!node) {
				return false;
			}
			const edge = this.workflowVersionEdges.find(({ id }) => id == node.edge);
			const parentId = edge?.source;
			if (!parentId) {
				return false;
			}
			const parentNode = this.workflowVersionNodes.find(({ id }) => id == parentId);
			return this.isLoopStart(parentNode);
		},
		runFeedbackFlow() {
			this.$emit('run-feedback-flow');
		},
		filterMainGraphEntries(entries) {
			return entries.filter(({ startNode }) => this.mainPathGraph.nodes.findIndex(({ id }) => id === startNode.id) !== -1);
		},
		replacePathWithNode(dag, startNode, endNode, type) {
			return FlowTreeService.replacePathWithNode(
				dag,
				startNode.id,
				endNode.id,
				{
					id: `${startNode.id}=>${endNode.id}->${type}-alternate`,
					type: `no-${type}-aqui`,
				},
				this.isAllowedToEdit
			);
		},
	},
	data() {
		return {
			nodeToFocus: null,
			showGraphErrorMessage: false,
		};
	},
	watch: {
		hasLoadedNodesData: {
			handler(val) {
				if (val) {
					setTimeout(() => {
						const nodeIdToFocus = this.editNodeId || this.rootNodeId || this.workflowVersionNodes[0]?.id;
						if (nodeIdToFocus) {
							this.focusOnNode(nodeIdToFocus);
						}
					}, 100);
				}
			},
			immediate: true,
		},
	},
};
</script>

<style scoped lang="scss">
.flow-tree-loader {
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
	width: 100%;
}
.tree-container {
	width: 100%;
	height: 100vh;
	padding-bottom: 60px;
}
::v-deep {
	.react-flow .react-flow__edge {
		pointer-events: none;
	}
}

.contact-us-button {
	height: 45px !important;
	font-size: 14px !important;
	border-radius: 6px !important;
	letter-spacing: 0px !important;
	color: var(--white) !important;
	font-weight: normal !important;
	text-transform: none !important;
	background: var(--bold-border-color) !important;
	border: 1px solid var(--bold-border-color) !important;
}
</style>
